import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Button,
  cn,
  Icon,
  Input,
  SearchableCombobox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nexus/ui";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import type {
  BigQueryUsageFeatureOption,
  BigQueryUsageTenantKind,
  BigQueryUsageTenantRef,
} from "@/features/bigquery-usage/api/bigquery-usage.api";
import { EventsTimeRangeControl } from "@/features/events/components/EventsTimeRangeControl";
import { TenantMark } from "@/features/llm-usage/components/TenantMark";
import { shortFingerprint } from "../lib/fingerprint-display";
import { humanizeBigQuerySlug } from "../lib/slug-labels";
import type { BigQueryUsageViewFilters } from "./BigQueryUsagePage";

interface BigQueryFilterBarProps {
  filters: BigQueryUsageViewFilters;
  /** From `/features` — the window's features, each with the service that owns it. */
  featureOptions: BigQueryUsageFeatureOption[];
  /** From `/tenants`, including the platform and unattributed buckets. */
  tenantOptions: BigQueryUsageTenantRef[];
  /** Both option lists are held behind the lead read, so they arrive late. */
  isOptionsLoading: boolean;
  resultCount: number | null;
  hasActiveFilters: boolean;
  onFiltersChange: (next: BigQueryUsageViewFilters) => void;
  onClearFilters: () => void;
  onClearRange: () => void;
  /** Rides at the end of the row, after the count. */
  actions?: ReactNode;
}

/** Neither picker can hold an empty value, so "no narrowing" needs a token. */
const ALL = "all";
const CACHE_HIT = "hit";
const CACHE_MISS = "miss";
const CONTROL_HEIGHT = "h-8";
/** Every keystroke would otherwise be four server aggregates. */
const SEARCH_DEBOUNCE_MS = 300;
const CHIP_CLASS =
  "inline-flex items-center gap-1.5 rounded-sm border border-primary/40 bg-primary/5 pl-2 pr-1 text-xs text-foreground";

/**
 * The feature and tenant filters are comboboxes rather than selects — both
 * lists run to everything that touched BigQuery in the window — so their
 * triggers borrow the select's shell to keep the three filters reading as one
 * row.
 */
const FILTER_TRIGGER_CLASS =
  "flex h-8 items-center justify-between gap-2 rounded-sm border border-input-border bg-input-background px-3 py-1 text-xs text-foreground outline-none transition-colors focus-visible:ring-1 focus-visible:ring-ring";
const ACTIVE_TRIGGER_CLASS = "border-primary/40 bg-primary/5 text-foreground";
/** A notch smaller than the table's mark; the trigger is only 32px tall. */
const TENANT_OPTION_MARK_SIZE = 16;

function tenantOptionValue(kind: BigQueryUsageTenantKind, tenantId: string | null): string {
  return `${kind}:${tenantId ?? ""}`;
}

/**
 * The tab's position, shown and undoable.
 *
 * Every control here reports upward rather than holding a value: the search
 * params are the position, so a narrowing that lived in this component would
 * survive a re-render but not a paste of the URL. The window is a scope rather
 * than a narrowing, which is why clear-all leaves it alone.
 */
export function BigQueryFilterBar({
  filters,
  featureOptions,
  tenantOptions,
  isOptionsLoading,
  resultCount,
  hasActiveFilters,
  onFiltersChange,
  onClearFilters,
  onClearRange,
  actions,
}: BigQueryFilterBarProps) {
  const { t, i18n } = useTranslation();

  const committedSearch = filters.search ?? "";
  const [searchInput, setSearchInput] = useState(committedSearch);
  const { debounced: debouncedSearchChange, cancel: cancelSearchChange } = useDebouncedCallback(
    (value: string) => {
      const term = value.trim();
      onFiltersChange({ ...filters, search: term === "" ? undefined : term });
    },
    SEARCH_DEBOUNCE_MS,
  );

  // Re-sync when the term is changed from outside the input — clear-all, or the
  // browser's back button walking to an earlier position.
  useEffect(() => {
    setSearchInput(committedSearch);
    cancelSearchChange();
  }, [committedSearch, cancelSearchChange]);

  const handleSearchClear = () => {
    cancelSearchChange();
    setSearchInput("");
    onFiltersChange({ ...filters, search: undefined });
  };

  const allFeaturesLabel = t("admin.bigQueryUsage.filters.allFeatures", "All features");
  const allTenantsLabel = t("admin.bigQueryUsage.filters.allTenants", "All tenants");

  const tenantLabel = (kind: BigQueryUsageTenantKind, tenantId: string | undefined): string => {
    if (kind === "platform") return t("admin.bigQueryUsage.tenantTable.platform", "Platform");
    if (kind === "unattributed") {
      return t("admin.bigQueryUsage.tenantTable.unattributed", "Unattributed");
    }
    const match = tenantOptions.find(
      (option) => option.kind === kind && option.tenantId === tenantId,
    );
    return match?.tenantName ?? tenantId ?? "";
  };

  const activeTenantValue =
    filters.tenantKind == null
      ? ALL
      : tenantOptionValue(filters.tenantKind, filters.tenantId ?? null);

  /**
   * A tenant narrowed to that this window's `/tenants` does not list — a pasted
   * link to a tenant with no rows here, or a window narrowed until it dropped
   * out. Both pickers take what the trigger shows from the item carrying the
   * value, so without one the trigger falls back to "All tenants" while the chip
   * beside it names the narrowing that is actually in force.
   */
  const isActiveTenantListed = tenantOptions.some(
    (option) => tenantOptionValue(option.kind, option.tenantId) === activeTenantValue,
  );

  /**
   * The unscoped row rides inside each list rather than beside it, which keeps
   * one code path for the keyboard, the search filter, and the trigger.
   */
  const featureItems = useMemo(
    () => [ALL, ...featureOptions.map((option) => option.feature)],
    [featureOptions],
  );

  const tenantItems = useMemo<BigQueryUsageTenantRef[]>(() => {
    const unscoped: BigQueryUsageTenantRef = {
      kind: "tenant",
      tenantId: ALL,
      tenantName: null,
      brandColor: null,
      logoUrl: null,
    };
    const listed = [unscoped, ...tenantOptions];
    if (filters.tenantKind == null || isActiveTenantListed) return listed;
    return [
      ...listed,
      {
        kind: filters.tenantKind,
        tenantId: filters.tenantId ?? null,
        tenantName: null,
        brandColor: null,
        logoUrl: null,
      },
    ];
  }, [filters.tenantId, filters.tenantKind, isActiveTenantListed, tenantOptions]);

  const featureItemLabel = (feature: string): string =>
    feature === ALL ? allFeaturesLabel : humanizeBigQuerySlug(feature);

  const tenantItemKey = (option: BigQueryUsageTenantRef): string =>
    option.tenantId === ALL ? ALL : tenantOptionValue(option.kind, option.tenantId);

  const tenantItemLabel = (option: BigQueryUsageTenantRef | null): string => {
    if (option == null || option.tenantId === ALL) return allTenantsLabel;
    return tenantLabel(option.kind, option.tenantId ?? undefined);
  };

  const handleFeatureSelect = (feature: string) => {
    onFiltersChange({ ...filters, feature: feature === ALL ? undefined : feature });
  };

  const handleTenantSelect = (option: BigQueryUsageTenantRef) => {
    if (option.tenantId === ALL) {
      onFiltersChange({ ...filters, tenantKind: undefined, tenantId: undefined });
      return;
    }
    onFiltersChange({
      ...filters,
      tenantKind: option.kind,
      tenantId: option.tenantId ?? undefined,
    });
  };

  const handleCacheChange = (value: string) => {
    onFiltersChange({
      ...filters,
      cacheHit: value === ALL ? undefined : value === CACHE_HIT,
    });
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      {/* The window scopes everything below it, so it leads the bar and is never
          swept up by clear-all. */}
      <EventsTimeRangeControl
        value={filters.range}
        onChange={(range) => onFiltersChange({ ...filters, range })}
      />

      {/* The one narrowing that reaches a row nothing on the page can be clicked
          to reach: the statement breakdown is a top-N list, and an admin holding
          a job id from Google's console has nothing to click at all. It carries
          no chip — unlike a chart-bucket narrowing, the term is already legible
          in the field, and the field clears itself. */}
      <div className="relative flex items-center">
        <Icon
          name="search"
          size="xs"
          className="pointer-events-none absolute left-2 text-muted-foreground"
          aria-hidden
        />
        <Input
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            debouncedSearchChange(event.target.value);
          }}
          aria-label={t("admin.bigQueryUsage.filters.searchLabel", "Search queries")}
          placeholder={t(
            "admin.bigQueryUsage.filters.searchPlaceholder",
            "Statement, job id, or correlation id…",
          )}
          className={cn(
            CONTROL_HEIGHT,
            "w-[240px] rounded-sm pl-7 pr-7 text-xs focus-visible:ring-0",
            committedSearch !== "" && ACTIVE_TRIGGER_CLASS,
          )}
        />
        {searchInput === "" ? null : (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="absolute right-1 size-5 rounded-sm"
            aria-label={t("admin.bigQueryUsage.filters.removeSearch", "Clear the search")}
            onClick={handleSearchClear}
          >
            <Icon name="x" size="xs" aria-hidden />
          </Button>
        )}
      </div>

      {/* Typed into rather than scrolled: every code path that has ever run a
          query lands in this list, and it grows with the product. */}
      <SearchableCombobox<string>
        items={featureItems}
        value={filters.feature ?? ALL}
        onSelect={handleFeatureSelect}
        getKey={(feature) => feature}
        getLabel={featureItemLabel}
        // The label is derived, but the slug is what an engineer arrives with
        // from a log line, so typing either one finds the feature.
        getSearchText={(feature) =>
          feature === ALL ? allFeaturesLabel : `${humanizeBigQuerySlug(feature)} ${feature}`
        }
        loading={isOptionsLoading && featureOptions.length === 0}
        ariaLabel={t("admin.bigQueryUsage.filters.featureLabel", "Filter by feature")}
        searchPlaceholder={t("admin.bigQueryUsage.filters.searchFeatures", "Search features…")}
        emptyMessage={t(
          "admin.bigQueryUsage.filters.noFeaturesMatch",
          "No features match your search.",
        )}
        renderItem={(feature) => (
          <span
            className="min-w-0 flex-1 truncate text-xs"
            title={feature === ALL ? undefined : feature}
          >
            {featureItemLabel(feature)}
          </span>
        )}
        renderTrigger={({ selected, open }) => (
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={t("admin.bigQueryUsage.filters.featureLabel", "Filter by feature")}
            className={cn(
              FILTER_TRIGGER_CLASS,
              "w-[200px]",
              filters.feature != null && ACTIVE_TRIGGER_CLASS,
              open && "border-input-focus-border",
            )}
          >
            <span className="truncate text-left">{featureItemLabel(selected ?? ALL)}</span>
            <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
          </button>
        )}
      />

      <SearchableCombobox<BigQueryUsageTenantRef>
        items={tenantItems}
        value={activeTenantValue}
        onSelect={handleTenantSelect}
        getKey={tenantItemKey}
        getLabel={(option) => tenantItemLabel(option)}
        loading={isOptionsLoading && tenantOptions.length === 0}
        ariaLabel={t("admin.bigQueryUsage.filters.tenantLabel", "Filter by tenant")}
        searchPlaceholder={t("admin.bigQueryUsage.filters.searchTenants", "Search tenants…")}
        emptyMessage={t(
          "admin.bigQueryUsage.filters.noTenantsMatch",
          "No tenants match your search.",
        )}
        renderItem={(option) => (
          <span className="flex min-w-0 flex-1 items-center gap-2 text-xs">
            <TenantOptionMark option={option} />
            <span className="truncate">{tenantItemLabel(option)}</span>
          </span>
        )}
        renderTrigger={({ selected, open }) => (
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={t("admin.bigQueryUsage.filters.tenantLabel", "Filter by tenant")}
            className={cn(
              FILTER_TRIGGER_CLASS,
              "w-[200px]",
              filters.tenantKind != null && ACTIVE_TRIGGER_CLASS,
              open && "border-input-focus-border",
            )}
          >
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <TenantOptionMark option={selected} />
              <span className="truncate text-left">{tenantItemLabel(selected)}</span>
            </span>
            <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
          </button>
        )}
      />

      {/* Three options and no growth, so this one stays a select — the same
          split the Token usage tab makes for the same reason. */}
      <Select
        value={filters.cacheHit == null ? ALL : filters.cacheHit ? CACHE_HIT : CACHE_MISS}
        onValueChange={handleCacheChange}
      >
        <SelectTrigger
          aria-label={t("admin.bigQueryUsage.filters.cacheLabel", "Filter by cache hit")}
          className={cn(
            CONTROL_HEIGHT,
            "w-[150px] rounded-sm text-xs",
            filters.cacheHit != null && ACTIVE_TRIGGER_CLASS,
          )}
        >
          <SelectValue placeholder={t("admin.bigQueryUsage.filters.anyCache", "Any cache state")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>
            {t("admin.bigQueryUsage.filters.anyCache", "Any cache state")}
          </SelectItem>
          <SelectItem value={CACHE_HIT}>
            {t("admin.bigQueryUsage.filters.cacheHitsOnly", "Cache hits only")}
          </SelectItem>
          <SelectItem value={CACHE_MISS}>
            {t("admin.bigQueryUsage.filters.cacheMissesOnly", "Cache misses only")}
          </SelectItem>
        </SelectContent>
      </Select>

      {/* One chip per active narrowing. The chart and the breakdown rows both
          write here, so a click on a bar cannot look like the tab broke. */}
      {filters.feature != null ? (
        <FilterChip
          label={t("admin.bigQueryUsage.filters.featureChip", "Feature: {{feature}}", {
            feature: humanizeBigQuerySlug(filters.feature),
          })}
          removeLabel={t("admin.bigQueryUsage.filters.removeFeature", "Remove the feature filter")}
          onRemove={() => onFiltersChange({ ...filters, feature: undefined })}
        />
      ) : null}

      {filters.tenantKind != null ? (
        <FilterChip
          label={t("admin.bigQueryUsage.filters.tenantChip", "Tenant: {{tenant}}", {
            tenant: tenantLabel(filters.tenantKind, filters.tenantId),
          })}
          removeLabel={t("admin.bigQueryUsage.filters.removeTenant", "Remove the tenant filter")}
          onRemove={() =>
            onFiltersChange({ ...filters, tenantKind: undefined, tenantId: undefined })
          }
        />
      ) : null}

      {filters.sqlFingerprint != null ? (
        <FilterChip
          label={t("admin.bigQueryUsage.filters.statementChip", "Statement: {{fingerprint}}", {
            fingerprint: shortFingerprint(filters.sqlFingerprint),
          })}
          removeLabel={t(
            "admin.bigQueryUsage.filters.removeStatement",
            "Remove the statement filter",
          )}
          onRemove={() => onFiltersChange({ ...filters, sqlFingerprint: undefined })}
        />
      ) : null}

      {/* Words and a glyph, not a tint: a hit filter and a miss filter are
          opposite questions and must not depend on hue to be told apart. */}
      {filters.cacheHit != null ? (
        <FilterChip
          icon={filters.cacheHit ? "check" : "x"}
          label={
            filters.cacheHit
              ? t("admin.bigQueryUsage.filters.cacheHitChip", "Cache: hits only")
              : t("admin.bigQueryUsage.filters.cacheMissChip", "Cache: misses only")
          }
          removeLabel={t("admin.bigQueryUsage.filters.removeCache", "Remove the cache filter")}
          onRemove={() => onFiltersChange({ ...filters, cacheHit: undefined })}
        />
      ) : null}

      {filters.since != null ? (
        <FilterChip
          label={t("admin.bigQueryUsage.filters.spanChip", "Time span {{range}}", {
            range: formatSpan(filters.since, filters.until, i18n.language),
          })}
          removeLabel={t("admin.bigQueryUsage.filters.removeSpan", "Remove the time span filter")}
          onRemove={onClearRange}
        />
      ) : null}

      {hasActiveFilters ? (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-primary"
          onClick={onClearFilters}
        >
          {t("admin.bigQueryUsage.filters.clear", "Clear filters")}
        </Button>
      ) : null}

      <span className="flex-1" />

      {/* A count of nothing is a real answer; a count nobody has yet is not. */}
      {resultCount == null ? null : (
        <span className="text-xs tabular-nums text-muted-foreground">
          {t("admin.bigQueryUsage.filters.resultCount", "{{count}} queries", {
            count: resultCount,
            formattedCount: resultCount.toLocaleString(i18n.language),
          })}
        </span>
      )}

      {actions}
    </div>
  );
}

/**
 * What an option wears on its left.
 *
 * R14 reaches into the dropdown: a named tenant gets its logo, the two special
 * categories keep the shield and the info glyph they carry everywhere else on
 * the tab, and the unscoped row takes none of them. A tenant the option list
 * does not carry has no name to draw from, so it gets the blank spacer that
 * keeps the column aligned rather than initials derived from a cuid.
 */
function TenantOptionMark({ option }: { option: BigQueryUsageTenantRef | null }) {
  if (option == null || option.tenantId === ALL) {
    return <Icon name="users" size="xs" color="muted" aria-hidden />;
  }
  if (option.kind !== "tenant") {
    return <Icon name={option.kind === "platform" ? "shield" : "info"} size="xs" aria-hidden />;
  }
  return (
    <TenantMark
      name={option.tenantName}
      logoUrl={option.logoUrl}
      brandColor={option.brandColor}
      size={TENANT_OPTION_MARK_SIZE}
    />
  );
}

function formatSpan(since: string, until: string | undefined, locale: string): string {
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  const from = new Date(since).toLocaleString(locale, options);
  if (until == null) return from;
  return `${from} – ${new Date(until).toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}

function FilterChip({
  label,
  removeLabel,
  onRemove,
  icon = "filter",
}: {
  label: string;
  removeLabel: string;
  onRemove: () => void;
  icon?: "filter" | "check" | "x";
}) {
  return (
    <span className={cn(CONTROL_HEIGHT, CHIP_CLASS)}>
      <Icon name={icon} size="xs" className="shrink-0 text-primary" aria-hidden />
      {label}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        className="size-5 rounded-sm"
        aria-label={removeLabel}
        onClick={onRemove}
      >
        <Icon name="x" size="xs" aria-hidden />
      </Button>
    </span>
  );
}
